import { useMutation } from '@tanstack/react-query';

import {
  IChannel,
  useInvalidateChannelById,
  useInvalidateChannelsList,
} from '@/entities/channel';
import { useInvalidateAllCompanyChannels } from '@/entities/company';
import { updateChannelById } from '@/shared/api/channels';
import { IUpdateChannelRequestDto } from '@/shared/api/channels/types';
import { useNotifications } from '@/shared/ui/Notifications';

export interface IUpdateMutationData extends IUpdateChannelRequestDto {
  channelId: string;
}

export function useUpdateChannel() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateChannelById = useInvalidateChannelById();
  const invalidateAllCompanyChannels = useInvalidateAllCompanyChannels();
  const { addNotification } = useNotifications();

  const updateChannelMutation = useMutation({
    mutationFn: ({ channelId, ...updateValues }: IUpdateMutationData) =>
      updateChannelById(channelId, updateValues),
    onSuccess: async (data) => {
      await invalidateChannelsList();
      await invalidateChannelById(data.id);
      await invalidateAllCompanyChannels();
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  const updateChannel = async (
    data: IUpdateMutationData,
    onChanged?: () => void
  ): Promise<IChannel> => {
    return updateChannelMutation.mutateAsync(data, { onSuccess: onChanged });
  };

  return { updateChannel, ...updateChannelMutation };
}
